import type { Game, RoomType } from '../../../shared/types.js';
import { ROOM_TYPES } from '../../../shared/types.js';
import { adminFetch, getAdminToken, setAdminToken, clearAdminToken } from '../admin';
import { el, relativeTime, showToast } from '../dom';
import { navigate } from '../router';
import { t, tPlural } from '../i18n';
import { buildLocalePicker } from '../i18n/picker';

interface AdminRoom {
  id: string;
  title: string;
  type: RoomType;
  createdAt: number;
  listed: boolean;
  playerCount: number;
}

interface AdminPageState {
  authed: boolean;
  loadingCatalog: boolean;
  catalog: Record<RoomType, Game[]> | null;
  rooms: AdminRoom[] | null;
  selectedTab: RoomType;
  pendingError: string | null;
}

const TYPE_ICON: Record<RoomType, string> = {
  games: '🎮',
  movies: '🎬',
  anime: '🌸',
};

function typeLabel(type: RoomType): string {
  return t(`type.${type}` as 'type.games');
}

const state: AdminPageState = {
  authed: !!getAdminToken(),
  loadingCatalog: false,
  catalog: null,
  rooms: null,
  selectedTab: 'games',
  pendingError: null,
};

let rerender: () => void = () => {};

export function buildAdminPage(onRerender: () => void): HTMLElement {
  rerender = onRerender;
  if (!state.authed) {
    return buildLogin();
  }
  if (state.catalog === null && !state.loadingCatalog) refreshAll();

  const layout = el('div', 'admin-layout');
  layout.appendChild(buildAdminHeader());
  layout.appendChild(buildCatalogSection());
  layout.appendChild(buildRoomsSection());
  return layout;
}

function buildLogin(): HTMLElement {
  const wrap = el('div', 'admin-login');
  const card = el('div', 'home-card');
  card.appendChild(el('h2', 'card-h', t('admin.signIn')));
  card.appendChild(el('p', 'form-hint', t('admin.signInHint')));
  if (state.pendingError) card.appendChild(el('div', 'admin-error', state.pendingError));

  const form = el('form', 'create-form') as HTMLFormElement;
  const tokenInput = document.createElement('input');
  tokenInput.type = 'password';
  tokenInput.className = 'form-input';
  tokenInput.placeholder = t('admin.tokenPlaceholder');
  tokenInput.autocomplete = 'off';
  form.appendChild(tokenInput);
  const submit = el('button', 'btn primary big', t('admin.signInButton')) as HTMLButtonElement;
  submit.type = 'submit';
  form.appendChild(submit);
  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const token = tokenInput.value.trim();
    if (!token) return;
    setAdminToken(token);
    try {
      const r = await adminFetch('/api/admin/catalog');
      if (r.ok) {
        state.authed = true;
        state.pendingError = null;
        rerender();
      } else {
        clearAdminToken();
        state.pendingError = r.status === 401 ? t('admin.invalidToken') : t('admin.authFailed', { status: r.status });
        rerender();
      }
    } catch (err) {
      clearAdminToken();
      state.pendingError = t('admin.networkError', { message: (err as Error).message });
      rerender();
    }
  });
  card.appendChild(form);

  // Locale picker on the login screen too
  const pickerWrap = el('div', 'admin-login-picker');
  pickerWrap.appendChild(buildLocalePicker());
  card.appendChild(pickerWrap);

  wrap.appendChild(card);
  return wrap;
}

function buildAdminHeader(): HTMLElement {
  const h = el('header', 'app-header');
  const left = el('div', 'header-left');
  const back = el('button', 'btn ghost small back-btn', t('room.back')) as HTMLButtonElement;
  back.addEventListener('click', () => navigate('/'));
  left.appendChild(back);
  left.appendChild(el('div', 'app-title', t('admin.title')));
  h.appendChild(left);
  const right = el('div', 'header-right');
  right.appendChild(buildLocalePicker());
  const refresh = el('button', 'btn ghost small', t('admin.refresh')) as HTMLButtonElement;
  refresh.addEventListener('click', () => refreshAll());
  right.appendChild(refresh);
  const signOut = el('button', 'btn ghost small', t('admin.signOut')) as HTMLButtonElement;
  signOut.addEventListener('click', () => {
    clearAdminToken();
    state.authed = false;
    rerender();
  });
  right.appendChild(signOut);
  h.appendChild(right);
  return h;
}

function buildCatalogSection(): HTMLElement {
  const sec = el('section', 'admin-section');
  sec.appendChild(el('h2', 'admin-section-h', t('admin.catalogs')));

  const tabs = el('div', 'admin-tabs');
  for (const ty of ROOM_TYPES) {
    const tab = el('button', `admin-tab ${state.selectedTab === ty ? 'on' : ''}`,
      `${TYPE_ICON[ty]} ${typeLabel(ty)}`) as HTMLButtonElement;
    tab.addEventListener('click', () => {
      state.selectedTab = ty;
      rerender();
    });
    tabs.appendChild(tab);
  }
  sec.appendChild(tabs);

  const body = el('div', 'admin-tab-body');
  if (!state.catalog) {
    body.appendChild(el('div', 'placeholder', state.loadingCatalog ? t('admin.loading') : t('admin.noData')));
  } else {
    body.appendChild(buildCatalogTab(state.selectedTab));
  }
  sec.appendChild(body);
  return sec;
}

function buildCatalogTab(type: RoomType): HTMLElement {
  const wrap = el('div', 'catalog-tab');
  const games = state.catalog?.[type] ?? [];

  const upload = el('label', 'logo-upload') as HTMLLabelElement;
  upload.appendChild(el('div', 'upload-icon', '⬆'));
  upload.appendChild(el('div', 'upload-text', t('admin.upload')));
  upload.appendChild(el('div', 'upload-hint', t('admin.uploadHint')));
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.png,.jpg,.jpeg,.webp,.avif,.svg,.gif';
  input.multiple = true;
  input.style.display = 'none';
  input.addEventListener('change', async () => {
    if (!input.files) return;
    for (const file of Array.from(input.files)) {
      await uploadLogo(type, file);
    }
    input.value = '';
    await refreshCatalog();
  });
  upload.appendChild(input);
  wrap.appendChild(upload);

  if (games.length === 0) {
    wrap.appendChild(el('div', 'placeholder', t('admin.noLogos')));
  } else {
    const grid = el('div', 'admin-logo-grid');
    for (const g of games) grid.appendChild(buildLogoCard(type, g));
    wrap.appendChild(grid);
  }
  return wrap;
}

function buildLogoCard(type: RoomType, game: Game): HTMLElement {
  const card = el('div', 'admin-logo-card');
  card.dataset.key = `logo-${type}-${game.id}`;
  if (game.logoUrl) {
    const img = document.createElement('img');
    img.src = game.logoUrl;
    img.alt = game.name;
    img.loading = 'lazy';
    card.appendChild(img);
  }
  card.appendChild(el('div', 'admin-logo-name', game.name));
  const filename = game.logoUrl ? decodeURIComponent(game.logoUrl.split('/').pop() ?? '') : '';
  card.appendChild(el('div', 'admin-logo-file', filename));
  const del = el('button', 'btn small danger', t('admin.delete')) as HTMLButtonElement;
  del.addEventListener('click', async () => {
    if (!confirm(t('admin.deleteLogoConfirm', { filename, type: typeLabel(type) }))) return;
    const r = await adminFetch(`/api/admin/catalog/${type}/${encodeURIComponent(filename)}`, { method: 'DELETE' });
    if (r.ok) {
      showToast(t('admin.deletedLogo', { filename }), 'info');
      await refreshCatalog();
    } else {
      const txt = await r.text().catch(() => '');
      showToast(t('admin.deleteLogoFail', { detail: txt || String(r.status) }), 'error');
    }
  });
  card.appendChild(del);
  return card;
}

async function uploadLogo(type: RoomType, file: File): Promise<void> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await adminFetch(`/api/admin/catalog/${type}`, { method: 'POST', body: fd });
  if (r.ok) {
    showToast(t('admin.uploadedLogo', { filename: file.name }), 'info');
  } else {
    const txt = await r.text().catch(() => '');
    showToast(t('admin.uploadFail', { detail: txt || String(r.status) }), 'error');
  }
}

function buildRoomsSection(): HTMLElement {
  const sec = el('section', 'admin-section');
  const head = el('div', 'admin-section-head');
  head.appendChild(el('h2', 'admin-section-h', t('admin.activeRooms')));
  sec.appendChild(head);

  if (!state.rooms) {
    sec.appendChild(el('div', 'placeholder', t('admin.loadingRooms')));
    return sec;
  }
  if (state.rooms.length === 0) {
    sec.appendChild(el('div', 'placeholder', t('admin.noActiveRooms')));
    return sec;
  }
  const list = el('ul', 'admin-room-list');
  for (const r of state.rooms) {
    const li = el('li', 'admin-room-row');
    li.dataset.key = `admin-room-${r.id}`;
    li.appendChild(el('span', 'room-row-icon', TYPE_ICON[r.type]));
    const main = el('div', 'room-row-main');
    main.appendChild(el('div', 'room-row-title', r.title));
    const sub = el('div', 'room-row-sub');
    sub.appendChild(el('span', 'room-row-code', r.id));
    sub.appendChild(el('span', 'sep', '·'));
    sub.appendChild(el('span', 'room-row-count',
      tPlural(r.playerCount, 'home.players_one', 'home.players_many', 'home.players_few')));
    sub.appendChild(el('span', 'sep', '·'));
    sub.appendChild(el('span', 'room-row-age', relativeTime(r.createdAt)));
    sub.appendChild(el('span', 'sep', '·'));
    sub.appendChild(el('span', 'room-row-listed', r.listed ? t('admin.listed') : t('admin.unlisted')));
    main.appendChild(sub);
    li.appendChild(main);

    const open = el('button', 'btn ghost small', t('admin.open')) as HTMLButtonElement;
    open.addEventListener('click', () => navigate(`/r/${r.id}`));
    li.appendChild(open);

    const reset = el('button', 'btn ghost small', t('admin.reset')) as HTMLButtonElement;
    reset.addEventListener('click', async () => {
      if (!confirm(t('admin.resetConfirm', { title: r.title, code: r.id }))) return;
      const resp = await adminFetch(`/api/admin/rooms/${r.id}/reset`, { method: 'POST' });
      if (resp.ok) showToast(t('admin.resetDone', { code: r.id }), 'info');
      else showToast(t('admin.resetFail', { status: resp.status }), 'error');
    });
    li.appendChild(reset);

    const del = el('button', 'btn small danger', t('admin.delete')) as HTMLButtonElement;
    del.addEventListener('click', async () => {
      if (!confirm(t('admin.deleteRoomConfirm', { title: r.title, code: r.id }))) return;
      const resp = await adminFetch(`/api/admin/rooms/${r.id}`, { method: 'DELETE' });
      if (resp.ok) {
        showToast(t('admin.deletedRoom', { code: r.id }), 'info');
        await refreshRooms();
      } else {
        showToast(t('admin.deleteRoomFail', { status: resp.status }), 'error');
      }
    });
    li.appendChild(del);
    list.appendChild(li);
  }
  sec.appendChild(list);
  return sec;
}

async function refreshAll(): Promise<void> {
  await Promise.all([refreshCatalog(), refreshRooms()]);
}

async function refreshCatalog(): Promise<void> {
  state.loadingCatalog = true;
  rerender();
  try {
    const r = await adminFetch('/api/admin/catalog');
    if (r.ok) {
      state.catalog = await r.json();
    } else if (r.status === 401) {
      clearAdminToken();
      state.authed = false;
      state.pendingError = t('admin.tokenRejected');
    }
  } catch (err) {
    showToast(t('admin.catalogFetchFail', { message: (err as Error).message }), 'error');
  } finally {
    state.loadingCatalog = false;
    rerender();
  }
}

async function refreshRooms(): Promise<void> {
  try {
    const r = await adminFetch('/api/admin/rooms');
    if (r.ok) state.rooms = await r.json();
  } catch (err) {
    showToast(t('admin.roomsFetchFail', { message: (err as Error).message }), 'error');
  }
  rerender();
}
